import React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS } from '@/constants';

import SleekToggleButtonGroup from '@/components/SleekToggleButtonGroup';
import Space from '@/components/Space';
import Paper from '@/components/Paper';

import Graph from './Graph';
import type { FlowDefinition } from './ClientServerGraph.types';

type FlowID = keyof typeof FLOWS;

interface Props {
  flows: Array<FlowID>;
  initialFlow: FlowID;
  omitFlags: boolean;
  marginTopOverride?: number;
}

function ClientServerGraph({
  flows,
  initialFlow,
  omitFlags,
  marginTopOverride,
}: Props) {
  const [type, setType] = React.useState<FlowID>(
    initialFlow || flows[0]
  );

  const { title, events } = FLOWS[type];

  const metrics = omitFlags ? {} : FLOWS[type].metrics;

  const singleFlow = flows.length === 1;

  if (singleFlow) {
    return (
      <StandaloneWrapper>
        <Graph
          {...metrics}
          width={670}
          columnWidth={18}
          events={events}
          marginTopOverride={marginTopOverride}
        />
      </StandaloneWrapper>
    );
  }

  const toggleOptions = flows.map((flowId) => ({
    label: FLOWS[flowId].toggleLabel,
    value: flowId,
  }));

  return (
    <Wrapper>
      <Header>
        <Title>{title}</Title>
        <SleekToggleButtonGroup
          value={type}
          options={toggleOptions}
          handleSelect={setType}
        />
      </Header>

      <Page>
        <Graph
          {...metrics}
          width={580}
          events={events}
          colorModeOverride="light"
          marginTopOverride={marginTopOverride}
        />
      </Page>
    </Wrapper>
  );
}

const StandaloneWrapper = styled.div`
  margin-left: -40px;
  margin-bottom: 50px;
  padding-left: 40px;
  /*
    Allow horizontal scrolling on mobile, when the graph won't fit
  */
  overflow: auto;

  @media ${BREAKPOINTS.mdAndSmaller} {
    margin-left: -16px;
  }
`;

const Wrapper = styled(Space)`
  margin-left: -16px;
  margin-right: -16px;
  margin-top: 48px;
  margin-bottom: 64px;
  padding: 16px;
  max-width: 738px;
  border-radius: 8px;

  html[data-color-mode='dark'] & {
    outline: 1px solid var(--color-gray-100);
  }
`;

const Page = styled(Paper)`
  padding: 0px 48px 32px;
  overflow: auto;
  max-width: 100%;
  border-radius: 4px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  margin-bottom: 16px;

  @media ${BREAKPOINTS.smAndSmaller} {
    justify-content: center;
  }
`;

const Title = styled.p`
  font-size: 1.25rem;
  font-weight: var(--font-weight-medium);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media ${BREAKPOINTS.smAndSmaller} {
    display: none;
  }
`;

const FLOWS: Record<string, FlowDefinition> = {
  csr: {
    title: 'Client Side Rendering',
    toggleLabel: 'CSR',
    metrics: {
      firstPaint: 17,
      contentPaint: 35,
      pageInteractive: 17,
    },
    events: [
      {
        key: 'initial-html',
        type: 'arrow',
        from: 'server',
        startAt: 0,
        duration: 4,
      },
      {
        key: 'download-js',
        type: 'event',
        eventType: 'javascript',
        track: 'client',
        startAt: 4,
        duration: 7,
        children: 'Download\nJavaScript',
      },
      {
        key: 'skeleton',
        type: 'event',
        eventType: 'render',
        track: 'client',
        startAt: 11,
        duration: 6,
        children: 'Render\nShell',
      },
      {
        key: 'fetch-request',
        type: 'arrow',
        from: 'client',
        startAt: 17,
        duration: 4,
      },
      {
        key: 'database-query',
        type: 'event',
        eventType: 'data',
        track: 'server',
        startAt: 21,
        duration: 5,
        children: 'Database Query',
      },
      {
        key: 'fetch-response',
        type: 'arrow',
        from: 'server',
        startAt: 26,
        duration: 4,
      },
      {
        key: 're-render',
        type: 'event',
        eventType: 'render',
        track: 'client',
        startAt: 30,
        duration: 5,
        children: 'Render\nContent',
      },
    ],
  },
  ssr: {
    title: 'Server Side Rendering',
    toggleLabel: 'SSR',
    metrics: {
      firstPaint: 7,
      contentPaint: 35,
      pageInteractive: 17,
    },
    events: [
      {
        key: 'skeleton',
        type: 'event',
        eventType: 'render',
        track: 'server',
        startAt: 0,
        duration: 3,
        children: 'Render\nShell',
        angle: -90,
        size: 'small',
      },
      {
        key: 'initial-html',
        type: 'arrow',
        from: 'server',
        startAt: 3,
        duration: 4,
      },
      {
        key: 'download-js',
        type: 'event',
        eventType: 'javascript',
        track: 'client',
        startAt: 7,
        duration: 7,
        children: 'Download\nJavaScript',
      },
      {
        key: 'hydrate',
        type: 'event',
        eventType: 'hydrate',
        track: 'client',
        startAt: 14,
        duration: 3,
        children: 'Hydrate',
        size: 'small',
        angle: -90,
      },
      {
        key: 'fetch-request',
        type: 'arrow',
        from: 'client',
        startAt: 17,
        duration: 4,
      },
      {
        key: 'database-query',
        type: 'event',
        eventType: 'data',
        track: 'server',
        startAt: 21,
        duration: 5,
        children: 'Database Query',
      },
      {
        key: 'fetch-response',
        type: 'arrow',
        from: 'server',
        startAt: 26,
        duration: 4,
      },
      {
        key: 're-render',
        type: 'event',
        eventType: 'render',
        track: 'client',
        startAt: 30,
        duration: 5,
        children: 'Render\nContent',
      },
    ],
  },
  next: {
    title: 'Legacy Next.js (pre-RSC)',
    toggleLabel: 'Before',
    metrics: {
      firstPaint: 15,
      contentPaint: 15,
      pageInteractive: 25,
    },
    events: [
      {
        key: 'database-query',
        type: 'event',
        eventType: 'data',
        track: 'server',
        startAt: 0,
        duration: 5,
        children: 'Database Query',
      },
      {
        key: 're-render',
        type: 'event',
        eventType: 'render',
        track: 'server',
        startAt: 5,
        duration: 6,
        children: 'Render\nApp',
      },
      {
        key: 'initial-html',
        type: 'arrow',
        from: 'server',
        startAt: 11,
        duration: 4,
      },
      {
        key: 'download-js',
        type: 'event',
        eventType: 'javascript',
        track: 'client',
        startAt: 15,
        duration: 7,
        children: 'Download\nJavaScript',
      },
      {
        key: 'hydrate',
        type: 'event',
        eventType: 'hydrate',
        track: 'client',
        startAt: 22,
        duration: 3,
        children: 'Hydrate',
        size: 'small',
        angle: -90,
      },
    ],
  },
  rsc: {
    title: 'Modern Next.js (with RSC)',
    toggleLabel: 'After',
    metrics: {
      firstPaint: 15,
      contentPaint: 15,
      pageInteractive: 23,
    },
    events: [
      {
        key: 'database-query',
        type: 'event',
        eventType: 'data',
        track: 'server',
        startAt: 0,
        duration: 5,
        children: 'Database Query',
      },
      {
        key: 're-render',
        type: 'event',
        eventType: 'render',
        track: 'server',
        startAt: 5,
        duration: 6,
        children: 'Render\nApp',
      },
      {
        key: 'initial-html',
        type: 'arrow',
        from: 'server',
        startAt: 11,
        duration: 4,
      },
      {
        key: 'download-js',
        type: 'event',
        eventType: 'javascript',
        track: 'client',
        startAt: 15,
        duration: 6,
        size: 'small',
        children: 'Download\nJavaScript',
      },
      {
        key: 'hydrate',
        type: 'event',
        eventType: 'hydrate',
        track: 'client',
        startAt: 21,
        duration: 2,
        children: 'Hydrate',
        size: 'small',
        angle: -90,
      },
    ],
  },
  suspense: {
    title: 'Streaming SSR with Suspense',
    toggleLabel: 'The Works',
    metrics: {
      firstPaint: 6,
      contentPaint: 18,
      pageInteractive: 13,
    },
    events: [
      {
        key: 'skeleton',
        type: 'event',
        eventType: 'render',
        track: 'server',
        startAt: 0,
        duration: 3,
        children: 'Render Shell',
        angle: -90,
        size: 'small',
      },
      {
        key: 'initial-html',
        type: 'arrow',
        from: 'server',
        startAt: 3,
        duration: 3,
      },
      {
        key: 'database-query',
        type: 'event',
        eventType: 'data',
        track: 'server',
        startAt: 3,
        duration: 8,
        children: 'Database\nQuery',
      },
      {
        key: 're-render',
        type: 'event',
        eventType: 'render',
        track: 'server',
        startAt: 11,
        duration: 3,
        children: 'Render\nContent',
        angle: -90,
        size: 'small',
      },
      {
        key: 'second-html',
        type: 'arrow',
        from: 'server',
        startAt: 14,
        duration: 4,
      },
      {
        key: 'download-js',
        type: 'event',
        eventType: 'javascript',
        track: 'client',
        startAt: 6,
        duration: 5,
        children: 'Download JS',
        size: 'small',
      },
      {
        key: 'hydrate',
        type: 'event',
        eventType: 'hydrate',
        track: 'client',
        startAt: 11,
        duration: 2,
        children: 'Hydrate',
        size: 'small',
        angle: -90,
      },
      {
        key: 'second-hydrate',
        type: 'event',
        eventType: 'hydrate',
        track: 'client',
        startAt: 18,
        duration: 2,
        children: 'Hydrate',
        size: 'small',
        angle: -90,
      },
    ],
  },
} as const;

export default ClientServerGraph;
